import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';
import { Lock, Loader2, AlertCircle, CheckCircle, RefreshCw, Smartphone, ShieldCheck, Copy } from 'lucide-react';

// Layered SuperAdmin login UI: OTP (email/SMS) → authenticator TOTP (or recovery code) → dashboard.
// Legacy accounts with no authenticator take the enrolment branch (scan QR → verify → recovery codes).
const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOTP, verifyTotp, enrolStart, enrolVerify, resendOTP } = useSuperAdminAuth();

  const tempToken = location.state?.tempToken || localStorage.getItem('superadmin_temp_token');
  const otpMethod = location.state?.otpMethod || localStorage.getItem('superadmin_otp_method');
  const maskedContact = location.state?.maskedContact || localStorage.getItem('superadmin_masked_contact');

  const [mode, setMode] = useState('otp'); // otp | totp | enrol | recovery
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [code, setCode] = useState('');
  const [preauthToken, setPreauthToken] = useState('');
  const [enrolToken, setEnrolToken] = useState('');
  const [enrolData, setEnrolData] = useState(null); // { otpauth_uri, manual_key }
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(180);

  useEffect(() => {
    if (!tempToken) { navigate('/superadmin-portal/login'); return; }
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [tempToken, navigate]);

  const clearTemp = () => {
    localStorage.removeItem('superadmin_temp_token');
    localStorage.removeItem('superadmin_otp_method');
    localStorage.removeItem('superadmin_masked_contact');
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
    if (index === 5 && updated.every(Boolean)) submitOtp(updated.join(''));
  };

  const submitOtp = async (otpValue = otp.join('')) => {
    if (otpValue.length !== 6) { setError('Please enter all 6 digits'); return; }
    setLoading(true); setError('');
    try {
      const data = await verifyOTP(tempToken, otpValue);
      if (data.stage === 'totp_required') {
        setPreauthToken(data.preauth_token);
        setMode('totp');
      } else if (data.stage === 'enrolment_required') {
        setEnrolToken(data.enrolment_token);
        const enr = await enrolStart(data.enrolment_token);
        if (enr.already_enrolled) { setPreauthToken(''); setMode('totp'); }
        else { setEnrolData(enr); setMode('enrol'); }
      } else {
        setError('Unexpected login state. Please sign in again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } finally { setLoading(false); }
  };

  const submitTotp = async () => {
    setLoading(true); setError('');
    try {
      await verifyTotp(preauthToken, code.trim());
      clearTemp();
      navigate('/superadmin-portal/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Incorrect code');
    } finally { setLoading(false); }
  };

  const submitEnrol = async () => {
    setLoading(true); setError('');
    try {
      const { recovery_codes } = await enrolVerify(enrolToken, code.trim());
      setRecoveryCodes(recovery_codes);
      clearTemp();
      setMode('recovery');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Incorrect code');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true); setError('');
    try {
      await resendOTP(tempToken);
      setCountdown(180);
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to resend OTP');
    } finally { setResending(false); }
  };

  const copy = (v) => { try { navigator.clipboard.writeText(v); } catch { /* no-op */ } };
  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            {mode === 'otp' ? <Lock className="h-8 w-8 text-purple-600" /> : <ShieldCheck className="h-8 w-8 text-purple-600" />}
          </div>
          {mode === 'otp' && (
            <>
              <h1 className="text-2xl font-bold">Verify Your Identity</h1>
              <p className="text-gray-600">Enter the 6-digit code sent to {otpMethod === 'email' ? 'your email' : 'your phone'}</p>
              {maskedContact && <p className="text-sm text-purple-600 mt-1">{maskedContact}</p>}
            </>
          )}
          {mode === 'totp' && (<><h1 className="text-2xl font-bold">Authenticator code</h1><p className="text-gray-600">Enter the 6-digit code from your authenticator app (or a recovery code).</p></>)}
          {mode === 'enrol' && (<><h1 className="text-2xl font-bold">Set up your authenticator</h1><p className="text-gray-600">Scan the QR code, then enter the 6-digit code to finish.</p></>)}
          {mode === 'recovery' && (<><h1 className="text-2xl font-bold">Save your recovery codes</h1><p className="text-gray-600">Store these safely — each works once if you lose your authenticator.</p></>)}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="text-red-600 h-5 w-5" /><span className="text-sm">{error}</span>
          </div>
        )}

        {/* Stage 2 — OTP */}
        {mode === 'otp' && (
          <>
            <div className="flex justify-center gap-3 mb-4">
              {otp.map((digit, index) => (
                <input key={index} id={`otp-${index}`} value={digit} maxLength={1}
                  onChange={(e) => handleOtpChange(index, e.target.value)} disabled={loading}
                  className="w-12 h-14 border text-center text-xl rounded-lg" />
              ))}
            </div>
            <p className="text-center text-sm mb-4">Expires in <strong>{formatTime(countdown)}</strong></p>
            <button onClick={() => submitOtp()} disabled={loading || otp.some((v) => !v)}
              className="w-full bg-purple-600 text-white py-3 rounded-lg disabled:opacity-50">
              {loading ? <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin h-5 w-5" />Verifying…</span> : 'Continue'}
            </button>
            <button onClick={handleResend} disabled={resending} className="w-full mt-3 text-purple-600 flex items-center justify-center gap-1">
              <RefreshCw className="h-4 w-4" />Resend Code
            </button>
          </>
        )}

        {/* Stage 3 — Authenticator TOTP / recovery code */}
        {mode === 'totp' && (
          <>
            <div className="flex items-center gap-2 text-gray-600 text-sm mb-3"><Smartphone className="h-4 w-4" /> Authenticator or recovery code</div>
            <input autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
              className="w-full border rounded-lg px-3 py-3 text-center tracking-widest text-lg mb-3" placeholder="123456" />
            <button onClick={submitTotp} disabled={loading || code.trim().length < 6}
              className="w-full bg-purple-600 text-white py-3 rounded-lg disabled:opacity-50">
              {loading ? 'Verifying…' : 'Verify & sign in'}
            </button>
          </>
        )}

        {/* Enrolment (legacy account) — scan QR */}
        {mode === 'enrol' && enrolData && (
          <>
            <div className="flex justify-center py-2">
              <div className="p-3 bg-white border rounded-xl"><QRCodeSVG value={enrolData.otpauth_uri} size={184} level="M" includeMargin /></div>
            </div>
            <div className="text-center mb-3">
              <button onClick={() => copy(enrolData.manual_key)} className="text-xs text-gray-500 inline-flex items-center gap-1 hover:text-gray-700">
                <Copy className="h-3 w-3" /> Can't scan? Copy setup key
              </button>
            </div>
            <input autoFocus inputMode="numeric" maxLength={8} value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-full border rounded-lg px-3 py-3 text-center tracking-widest text-lg mb-3" placeholder="123456" />
            <button onClick={submitEnrol} disabled={loading || code.trim().length < 6}
              className="w-full bg-purple-600 text-white py-3 rounded-lg disabled:opacity-50">
              {loading ? 'Verifying…' : 'Enrol & sign in'}
            </button>
          </>
        )}

        {/* Recovery codes */}
        {mode === 'recovery' && (
          <>
            <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="text-green-600 h-5 w-5" /><span className="text-sm">Authenticator enrolled.</span>
            </div>
            <div className="grid grid-cols-2 gap-2 bg-gray-50 border rounded-lg p-3 font-mono text-xs mb-2">
              {recoveryCodes.map((c) => <div key={c} className="text-gray-800">{c}</div>)}
            </div>
            <button onClick={() => copy(recoveryCodes.join('\n'))} className="text-xs text-gray-500 inline-flex items-center gap-1 hover:text-gray-700 mb-3">
              <Copy className="h-3 w-3" /> Copy all codes
            </button>
            <button onClick={() => navigate('/superadmin-portal/dashboard')} className="w-full bg-purple-600 text-white py-3 rounded-lg">
              I've saved them — continue
            </button>
          </>
        )}

        <div className="mt-6 text-center">
          <button onClick={() => navigate('/superadmin-portal/login')} className="text-sm text-gray-600">← Back to login</button>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
